import React, { useRef, useEffect, useState, useCallback } from "react"
import PropTypes from "prop-types"
import { PlayerSdk } from "@api.video/player-sdk"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCirclePlay, faCirclePause } from "@fortawesome/pro-solid-svg-icons"
import { Icon, VideoContainer, VideoWrapper, VignetteOverlay, IntroduceLabel, DimOverlay } from "./styles"

const VideoPlayer = ({ videoId, onPlay, displayLabel, displayEmployerLabel, fullSize, videoHeight, videoWidth }) => {
  const videoRef = useRef(null)
  const [player, setPlayer] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const isMounted = useRef(true) // Track if the component is mounted

  const initializePlayer = useCallback(() => {
    const actualVideoId = videoId.startsWith("encoding-") ? videoId.slice(9) : videoId

    if (actualVideoId && videoRef.current) {
      // Destroy previous player instance if it exists
      if (player) {
        player.destroy()
      }

      const newPlayer = new PlayerSdk(videoRef.current, {
        id: actualVideoId,
        hideTitle: true,
        hideControls: true
      })

      // Set initial volume to 50%
      newPlayer.setVolume(0.5)

      // Use local functions for event handlers
      const handlePlay = () => {
        if (isMounted.current) setIsPlaying(true)
      }
      const handlePause = () => {
        if (isMounted.current) setIsPlaying(false)
      }

      newPlayer.addEventListener("play", handlePlay)
      newPlayer.addEventListener("pause", handlePause)

      // Store these handlers on the player instance for later removal
      newPlayer.customHandlers = { handlePlay, handlePause }

      setPlayer(newPlayer)
    }
  }, [videoId])

  useEffect(() => {
    isMounted.current = true
    initializePlayer()

    // Cleanup when the component unmounts
    return () => {
      isMounted.current = false
      if (player) {
        // Pause the video before destroying the player
        player.pause()
        player.destroy()
      }
    }
  }, [initializePlayer])

  const handleClick = () => {
    if (player) {
      if (isPlaying) {
        player.pause()
      } else {
        player.play()
        if (onPlay) onPlay()
      }
    }
  }

  return (
    <VideoWrapper fullSize={fullSize} onClick={handleClick}>
      {!isPlaying && <DimOverlay />}

      <VideoContainer ref={videoRef} fullSize={fullSize} width={videoWidth} height={videoHeight} />

      {!videoId.includes("encoding-") && (
        <>
          {!isPlaying && (
            <>
              <Icon className="play-button">
                <FontAwesomeIcon icon={faCirclePlay} />
              </Icon>

              {displayLabel && <IntroduceLabel src="/assets/introduce.png" />}
              {displayEmployerLabel && <IntroduceLabel src="/assets/introduce-employer.png" />}
            </>
          )}

          {isPlaying && (
            <Icon className="pause-button">
              <FontAwesomeIcon icon={faCirclePause} />
            </Icon>
          )}
        </>
      )}

      <VignetteOverlay />
    </VideoWrapper>
  )
}

VideoPlayer.propTypes = {
  videoId: PropTypes.string.isRequired,
  onPlay: PropTypes.func,
  displayLabel: PropTypes.bool,
  displayEmployerLabel: PropTypes.bool,
  fullSize: PropTypes.bool
}

VideoPlayer.defaultProps = {
  onPlay: undefined,
  displayLabel: false,
  displayEmployerLabel: false,
  fullSize: false
}

export default VideoPlayer
